// Global Instructions Rule Applied!
//
// Company short descriptions (2026-11-03): the short "what the company does"
// reports print. An agent writes the missing ones; an agent-written text
// prints at once but reads "agent-written" until a person signs it off on
// the company form or edits it.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

/** none | written (by a person) | unreviewed (agent-written, not signed off) | signed_off */
export const ShortDescriptionStatus = {
  none: 'none',
  written: 'written',
  unreviewed: 'unreviewed',
  signedOff: 'signed_off'
} as const

// company_short_description__get / __set / __sign_off
export const CompanyShortDescriptionSchema = z
  .object({
    accounts__id: z.string(),
    accounts__short_description: z.string().nullable(),
    accounts__short_description_agent_written: z.boolean().nullable(),
    accounts__short_description_generated_at: z.string().nullable(),
    accounts__short_description_signed_off_at: z.string().nullable(),
    prog__signed_off_by_name: z.string().nullable(),
    prog__status: z.enum(['none', 'written', 'unreviewed', 'signed_off']),
    prog__max_chars: z.number()
  })
  .strict()
export type CompanyShortDescription = z.infer<typeof CompanyShortDescriptionSchema>
export const validateCompanyShortDescription = (data: unknown): CompanyShortDescription =>
  validateWithZodSchema(CompanyShortDescriptionSchema, data, 'company short description') as CompanyShortDescription

const CompanyRefSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string().nullable(),
    // the agent's own words when it declined or the call failed
    prog__reason: z.string().nullish()
  })
  .strict()
export type CompanyRef = z.infer<typeof CompanyRefSchema>

// company_short_descriptions__status (+ the cfg length cap)
export const CompanyShortDescriptionsStatusSchema = z
  .object({
    portfolio_total: z.number(),
    written: z.number(),
    signed_off: z.number(),
    unreviewed: z.number(),
    to_generate: z.number(),
    cost_today_usd: z.number().nullable(),
    max_chars: z.number(),
    unreviewed_companies: z.array(CompanyRefSchema),
    no_source_companies: z.array(CompanyRefSchema),
    declined_companies: z.array(CompanyRefSchema)
  })
  .strict()
export type CompanyShortDescriptionsStatus = z.infer<typeof CompanyShortDescriptionsStatusSchema>
export const validateCompanyShortDescriptionsStatus = (data: unknown): CompanyShortDescriptionsStatus =>
  validateWithZodSchema(
    CompanyShortDescriptionsStatusSchema,
    data,
    'company short descriptions status'
  ) as CompanyShortDescriptionsStatus

// One Generate batch
export const ShortDescriptionsBatchSchema = z
  .object({
    written: z.array(CompanyRefSchema.extend({ accounts__short_description: z.string().nullable() }).strict()),
    declined: z.array(CompanyRefSchema),
    failed: z.array(CompanyRefSchema),
    superseded: z.number(),
    remaining: z.number(),
    cost_usd: z.number()
  })
  .strict()
export type ShortDescriptionsBatch = z.infer<typeof ShortDescriptionsBatchSchema>
export const validateShortDescriptionsBatch = (data: unknown): ShortDescriptionsBatch =>
  validateWithZodSchema(ShortDescriptionsBatchSchema, data, 'short descriptions batch') as ShortDescriptionsBatch

// What a rendered report says about the short descriptions it prints (the
// X-Document-Report-Descriptions header): the companies whose agent-written
// text nobody has signed off yet.
export const ReportDescriptionsStatusSchema = z.object({ unreviewed: z.array(z.string()) }).strict()
export type ReportDescriptionsStatus = z.infer<typeof ReportDescriptionsStatusSchema>
export const validateReportDescriptionsStatus = (data: unknown): ReportDescriptionsStatus =>
  validateWithZodSchema(ReportDescriptionsStatusSchema, data, 'report descriptions status') as ReportDescriptionsStatus
